// Pure data layout — given the awning's footprint, return an array of
// furniture pieces sized + positioned to fit on the patio. Positions are
// anchored to the wall (wallZ) so furniture stays put as the user retracts
// the awning; bounds use the patio dimensions so larger awnings get a
// proportionally larger seating area.
//
// No Three.js dependencies — pure numbers/strings, easy to unit-test.

const X_MARGIN = 0.30;       // 30 cm clearance from each lateral edge
const Z_BACK   = 0.05;       // 5 cm clearance from the wall plane
const Z_FRONT  = 0.30;       // 30 cm clearance from the patio's front edge

// Distance from the wall front face to the back edge of the rearmost
// furniture piece. 0.9 m ≈ 3 ft of standing/walking room behind the seating.
const WALL_BACK_GAP = 0.9;

// Width-tier thresholds (meters). Match the WIDTH_FT keys in AwningBuilder
// — Small ≈ 3.35 m, Medium ≈ 4.57 m, Large ≈ 6.10 m.
const MEDIUM_THRESHOLD = 3.65;
const LARGE_THRESHOLD  = 5.50;

const DEG = Math.PI / 180;

/**
 * @param {number} awningWidthM       — awning width (meters, X axis)
 * @param {number} awningProjectionM  — awning's full projection (meters, +Z from wall)
 * @param {number} wallZ              — world-Z of the wall front face
 * @param {number} patioWidth         — patio X extent (≥ awningWidthM + 1.5)
 * @param {number} patioDepth         — patio Z extent (≥ awningProjectionM + 1.5)
 * @returns {Array<FurniturePiece>}
 */
export function computeFurnitureLayout(awningWidthM, awningProjectionM, wallZ, patioWidth, patioDepth) {
  // Bounds use PATIO dimensions, not awning footprint — the patio extends
  // past the awning so furniture can spill slightly into the unshaded
  // overhang area for visual breathing room. fitsInBounds enforces only
  // that pieces stay on the patio surface.
  const xMin = -patioWidth / 2 + X_MARGIN;
  const xMax =  patioWidth / 2 - X_MARGIN;
  const zMin = wallZ + Z_BACK;
  const zMax = wallZ + patioDepth - Z_FRONT;

  let pieces;
  if (awningWidthM < MEDIUM_THRESHOLD) {
    pieces = layoutSmall(awningWidthM, wallZ);
  } else if (awningWidthM < LARGE_THRESHOLD) {
    pieces = layoutMedium(awningWidthM, wallZ);
  } else {
    pieces = layoutLarge(awningWidthM, wallZ);
  }

  return pieces.filter(p => fitsInBounds(p, xMin, xMax, zMin, zMax));
}

export function getTemplateName(awningWidthM) {
  if (awningWidthM < MEDIUM_THRESHOLD) return 'small';
  if (awningWidthM < LARGE_THRESHOLD)  return 'medium';
  return 'large';
}

// ── Helpers ─────────────────────────────────────────────────────────

function rotatedHalfExtents(width, depth, rotY) {
  const c = Math.abs(Math.cos(rotY));
  const s = Math.abs(Math.sin(rotY));
  return {
    halfX: c * width / 2 + s * depth / 2,
    halfZ: s * width / 2 + c * depth / 2,
  };
}

function fitsInBounds(piece, xMin, xMax, zMin, zMax) {
  const ext = rotatedHalfExtents(
    piece.dimensions.width,
    piece.dimensions.depth,
    piece.rotationY,
  );
  const x = piece.position.x;
  const z = piece.position.z;
  return (x - ext.halfX >= xMin) && (x + ext.halfX <= xMax)
      && (z - ext.halfZ >= zMin) && (z + ext.halfZ <= zMax);
}

// Z position whose REAR edge sits WALL_BACK_GAP forward of the wall.
function backAnchored(wallZ, depth) {
  return wallZ + WALL_BACK_GAP + depth / 2;
}

// ── SMALL — mini conversation set: loveseat + coffee table + accent chair ──
function layoutSmall(widthM, wallZ) {
  const loveW = 1.52, loveD = 0.86, loveH = 0.78;
  const ctW   = 0.91, ctD   = 0.51, ctH   = 0.41;
  const acW   = 0.81, acD   = 0.91, acH   = 0.85;
  const acRot = Math.PI / 4;

  const loveZ = backAnchored(wallZ, loveD);
  // Coffee table 0.5 m forward of the loveseat's front face.
  const ctZ = loveZ + loveD / 2 + 0.50 + ctD / 2;
  // Accent chair offset to the LEFT, slightly forward of the loveseat.
  const acExt = rotatedHalfExtents(acW, acD, acRot);
  const acX = -widthM / 2 + acW / 2 + 0.40;
  const acZ = loveZ + 0.30;

  return [
    {
      type: 'sectional',                              // _buildSofa(withPillows=true)
      position: { x: 0, y: 0, z: loveZ },
      rotationY: 0,
      dimensions: { width: loveW, depth: loveD, height: loveH },
    },
    {
      type: 'coffee-table',
      position: { x: 0, y: 0, z: ctZ },
      rotationY: 0,
      dimensions: { width: ctW, depth: ctD, height: ctH },
    },
    {
      type: 'lounge-chair',                           // _buildSofa(withPillows=false)
      position: { x: acX, y: 0, z: acZ },
      rotationY: acRot,
      dimensions: { width: acW, depth: acD, height: acH },
    },
  ];
}

// ── MEDIUM — wicker L-sectional + coffee table ─────────────────────
function layoutMedium(widthM, wallZ) {
  const sectionalW = Math.min(2.44, widthM * 0.72);
  const sectionalD = 1.52;
  const sectionalH = 0.78;
  const ctW = 0.91, ctD = 0.61, ctH = 0.41;

  const sectZ = backAnchored(wallZ, sectionalD);
  const ctZ   = sectZ + sectionalD / 2 + 0.50 + ctD / 2;

  return [
    {
      type: 'sectional',
      position: { x: 0, y: 0, z: sectZ },
      rotationY: 0,
      dimensions: { width: sectionalW, depth: sectionalD, height: sectionalH },
    },
    {
      type: 'coffee-table',
      position: { x: 0, y: 0, z: ctZ },
      rotationY: 0,
      dimensions: { width: ctW, depth: ctD, height: ctH },
    },
  ];
}

// ── LARGE — L-sectional + coffee table, open side facing yard ──────
function layoutLarge(widthM, wallZ) {
  const sectionalH = 0.78;
  const longW = 2.74, longD = 0.91;
  const shortDepthAxis = 0.91;
  const shortLongAxis  = 1.83;
  const shortRot = Math.PI / 2;
  const ctW = 1.07, ctD = 0.61, ctH = 0.41;

  // Long arm along the LEFT side of the patio, back anchored 0.9 m forward of wall.
  const leftCx = -widthM / 2 + longW / 2;
  const longZ  = backAnchored(wallZ, longD);

  // Short arm extends forward (+Z) from the long arm's left end.
  const shortCx = (leftCx - longW / 2) + shortDepthAxis / 2;
  const shortZ  = (longZ + longD / 2) + shortLongAxis / 2;

  // Coffee table in the open corner of the L: 0.5 m forward of the inner
  // corner where the two arms meet, X-centered in the L's interior.
  const innerCornerX = shortCx + shortDepthAxis / 2;
  const innerCornerZ = longZ + longD / 2;
  const interiorRightX = leftCx + longW / 2;
  const ctX = (innerCornerX + interiorRightX) / 2;
  const ctZ = innerCornerZ + 0.50 + ctD / 2;

  return [
    {
      type: 'sectional',
      position: { x: leftCx, y: 0, z: longZ },
      rotationY: 0,
      dimensions: { width: longW, depth: longD, height: sectionalH },
    },
    {
      type: 'sectional',
      position: { x: shortCx, y: 0, z: shortZ },
      rotationY: shortRot,
      dimensions: { width: shortLongAxis, depth: shortDepthAxis, height: sectionalH },
    },
    {
      type: 'coffee-table',
      position: { x: ctX, y: 0, z: ctZ },
      rotationY: 0,
      dimensions: { width: ctW, depth: ctD, height: ctH },
    },
  ];
}
